/**
 * API Service para el módulo de Muestras
 * Usa autenticación JWT unificada del sistema principal
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Helper para obtener el token - ahora usa el token principal
const getToken = () => localStorage.getItem('token');

// Headers con autenticación
const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`
});

// Helper para hacer requests
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE}/api/muestras${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders(),
      ...options.headers
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Error de servidor' }));
    throw new Error(error.detail || `Error ${response.status}`);
  }

  return response.json();
};

// ============================================================
// DASHBOARD
// ============================================================
export const getDashboardStats = () => apiRequest('/dashboard');
export const getHistorialStats = () => apiRequest('/historial/stats');

// ============================================================
// TELAS
// ============================================================
export const getTelas = () => apiRequest('/telas');
export const getTela = (id) => apiRequest(`/telas/${id}`);
export const createTela = (data) => apiRequest('/telas', { method: 'POST', body: JSON.stringify(data) });
export const updateTela = (id, data) => apiRequest(`/telas/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTela = (id) => apiRequest(`/telas/${id}`, { method: 'DELETE' });

// ============================================================
// ENTALLES
// ============================================================
export const getEntalles = () => apiRequest('/entalles');
export const getEntalle = (id) => apiRequest(`/entalles/${id}`);
export const createEntalle = (data) => apiRequest('/entalles', { method: 'POST', body: JSON.stringify(data) });
export const updateEntalle = (id, data) => apiRequest(`/entalles/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteEntalle = (id) => apiRequest(`/entalles/${id}`, { method: 'DELETE' });

// ============================================================
// TIPOS DE PRODUCTO
// ============================================================
export const getTiposProducto = () => apiRequest('/tipos-producto');
export const getTipoProducto = (id) => apiRequest(`/tipos-producto/${id}`);
export const createTipoProducto = (data) => apiRequest('/tipos-producto', { method: 'POST', body: JSON.stringify(data) });
export const updateTipoProducto = (id, data) => apiRequest(`/tipos-producto/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTipoProducto = (id) => apiRequest(`/tipos-producto/${id}`, { method: 'DELETE' });

// ============================================================
// MARCAS
// ============================================================
export const getMarcas = () => apiRequest('/marcas');
export const getMarca = (id) => apiRequest(`/marcas/${id}`);
export const createMarca = (data) => apiRequest('/marcas', { method: 'POST', body: JSON.stringify(data) });
export const updateMarca = (id, data) => apiRequest(`/marcas/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteMarca = (id) => apiRequest(`/marcas/${id}`, { method: 'DELETE' });

// ============================================================
// HILOS
// ============================================================
export const getHilos = () => apiRequest('/hilos');
export const getHilo = (id) => apiRequest(`/hilos/${id}`);
export const createHilo = (data) => apiRequest('/hilos', { method: 'POST', body: JSON.stringify(data) });
export const updateHilo = (id, data) => apiRequest(`/hilos/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteHilo = (id) => apiRequest(`/hilos/${id}`, { method: 'DELETE' });

// ============================================================
// MUESTRAS BASE
// ============================================================
export const getMuestrasBase = () => apiRequest('/muestras-base');
export const getMuestraBase = (id) => apiRequest(`/muestras-base/${id}`);
export const createMuestraBase = (data) => apiRequest('/muestras-base', { method: 'POST', body: JSON.stringify(data) });
export const updateMuestraBase = (id, data) => apiRequest(`/muestras-base/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteMuestraBase = (id) => apiRequest(`/muestras-base/${id}`, { method: 'DELETE' });

// ============================================================
// BASES
// ============================================================
export const getBases = () => apiRequest('/bases');
export const getBase = (id) => apiRequest(`/bases/${id}`);
export const createBase = (data) => apiRequest('/bases', { method: 'POST', body: JSON.stringify(data) });
export const updateBase = (id, data) => apiRequest(`/bases/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteBase = (id) => apiRequest(`/bases/${id}`, { method: 'DELETE' });

// ============================================================
// TIZADOS
// ============================================================
export const getTizados = () => apiRequest('/tizados');
export const getTizado = (id) => apiRequest(`/tizados/${id}`);
export const createTizado = (data) => apiRequest('/tizados', { method: 'POST', body: JSON.stringify(data) });
export const updateTizado = (id, data) => apiRequest(`/tizados/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTizado = (id) => apiRequest(`/tizados/${id}`, { method: 'DELETE' });

// ============================================================
// FICHAS
// ============================================================
export const getFichas = () => apiRequest('/fichas');
export const getFichasByBase = (baseId) => apiRequest(`/fichas/base/${baseId}`);
export const createFicha = (data) => apiRequest('/fichas', { method: 'POST', body: JSON.stringify(data) });
export const deleteFicha = (id) => apiRequest(`/fichas/${id}`, { method: 'DELETE' });

export const uploadFichaFile = async (fichaId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await fetch(`${API_BASE}/api/muestras/fichas/${fichaId}/archivo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getToken()}`
    },
    body: formData
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Error subiendo archivo' }));
    throw new Error(error.detail);
  }
  
  return response.json();
};

// ============================================================
// USUARIOS
// ============================================================
export const getUsuarios = () => apiRequest('/usuarios');
export const getUsuario = (id) => apiRequest(`/usuarios/${id}`);
export const createUsuario = (data) => apiRequest('/usuarios', { method: 'POST', body: JSON.stringify(data) });
export const updateUsuario = (id, data) => apiRequest(`/usuarios/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteUsuario = (id) => apiRequest(`/usuarios/${id}`, { method: 'DELETE' });
export const getUsuarioPermisos = (id) => apiRequest(`/usuarios/${id}/permisos`);
export const updateUsuarioPermisos = (id, data) => apiRequest(`/usuarios/${id}/permisos`, { method: 'PUT', body: JSON.stringify(data) });

// ============================================================
// HISTORIAL
// ============================================================
export const getHistorial = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiRequest(`/historial${query ? `?${query}` : ''}`);
};
export const getHistorialTablas = () => apiRequest('/historial/tablas');

// ============================================================
// ADMINISTRACIÓN
// ============================================================
export const resetAllMuestras = () => apiRequest('/reset-all', { method: 'DELETE' });

// ============================================================
// ARCHIVOS - Upload
// ============================================================
export const uploadFile = async (file, table, recordId, fieldName) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await fetch(`${API_BASE}/api/muestras/${table}/${recordId}/${fieldName}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getToken()}`
    },
    body: formData
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Error subiendo archivo' }));
    throw new Error(error.detail);
  }
  
  return response.json();
};

export const deleteFile = async (table, recordId, fieldName) => {
  return apiRequest(`/${table}/${recordId}/${fieldName}`, { method: 'DELETE' });
};

export const getFileUrl = (filename, forDownload = false) => {
  if (!filename) return null;
  // Si ya es una URL completa, retornarla
  if (filename.startsWith('http')) return filename;
  // Construir URL de R2
  const base = `${API_BASE}/api/muestras/files/${filename}`;
  return forDownload ? `${base}?download=true` : base;
};

// ============================================================
// UPLOAD GENÉRICO (para bases - imagen, patron)
// ============================================================
export const uploadBaseFile = async (baseId, fieldName, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await fetch(`${API_BASE}/api/muestras/bases/${baseId}/${fieldName}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getToken()}`
    },
    body: formData
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Error subiendo archivo' }));
    throw new Error(error.detail);
  }
  
  return response.json();
};

export const uploadTizadoFile = async (tizadoId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await fetch(`${API_BASE}/api/muestras/tizados/${tizadoId}/archivo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getToken()}`
    },
    body: formData
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Error subiendo archivo' }));
    throw new Error(error.detail);
  }
  
  return response.json();
};

export const uploadMuestraBaseCosto = async (muestraId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await fetch(`${API_BASE}/api/muestras/muestras-base/${muestraId}/archivo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getToken()}`
    },
    body: formData
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Error subiendo archivo' }));
    throw new Error(error.detail);
  }
  
  return response.json();
};
